"use client";

import { type FormEvent, useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useRun } from "@/components/tools/reference-tools/use-run";
import { JournalCard } from "@/components/tools/research-discovery/journal-card";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type JournalMetrics,
  type LookupProblem,
  lookUpJournal,
} from "@/lib/tool-kit/research-discovery/journal-metrics";

/** English strings. A translated content file overrides these through `ui`. */
export const JOURNAL_METRICS_LOOKUP_STRINGS = {
  inputLabel: "Journal title or ISSN",
  inputPlaceholder:
    "Proceedings of the National Academy of Sciences, or 0027-8424",
  inputHint:
    "Type part of a title, or an ISSN (print or online). Pressing Look up sends it from your browser to OpenAlex's public API — nothing goes to CitationEasy.",
  lookUp: "Look up",
  looking: "Looking up…",
  emptyPrompt: "Enter a journal title or ISSN to see its OpenAlex metrics.",
  tooShort: "Type at least two characters of the journal's title.",
  badCheckDigit:
    "That ISSN's check digit does not add up, so it may be mistyped — check that the journal below is the one you meant.",
  notFound:
    "OpenAlex has no journal for that ISSN. Check the number, try the other ISSN (print or online), or search by title.",
  noMatches:
    "No OpenAlex source matches that title. Try fewer words, or the journal's ISSN.",
  rateLimited:
    "OpenAlex's free request budget for your network is used up for now. Try again later.",
  failed:
    "The lookup failed — OpenAlex did not answer. Check your connection and try again.",
  resultsHeading: "Journal metrics",
  matchesLabel: "Other matches — choose one",
  matchOption: "{name} · {works} works",
  types: {
    journal: "Journal",
    repository: "Repository",
    "book series": "Book series",
    conference: "Conference",
    "ebook platform": "E-book platform",
    metadata: "Metadata source",
    other: "Other",
  },
  activeYears: "Publishing {first}–{last}",
  alsoKnown: "Also known as:",
  issnL: "ISSN-L",
  works: "Works",
  citations: "Citations",
  hIndex: "h-index",
  i10Index: "i10-index",
  meanCitedness: "2-year mean citedness",
  oaShare: "Open-access works",
  notJif:
    "These are OpenAlex's own metrics, calculated from OpenAlex's data. They are not Clarivate's Journal Impact Factor (JIF) or Elsevier's CiteScore, and they will not match those figures. For an official JIF or CiteScore, check Journal Citation Reports or Scopus through your library.",
  accessHeading: "Open access and indexing",
  colFeature: "Feature",
  colValue: "In OpenAlex",
  yes: "Yes",
  no: "No",
  fullyOa: "Fully open-access journal",
  inDoaj: "Listed in the Directory of Open Access Journals",
  doajSince: "Yes, since {year}",
  highOaRate: "High share of open-access articles",
  apc: "Article processing charge (list price)",
  apcNone: "None listed",
  inMedline: "Indexed in MEDLINE",
  inCore: "On the CWTS Core list",
  apcNote:
    "An APC is the publisher's advertised list price, not what any author paid; waivers and discounts are common. “None listed” means OpenAlex has no price on record, not that the journal is free to publish in.",
  yearsHeading: "Recent years",
  colYear: "Year",
  colWorks: "Works published",
  colOaShare: "Open access",
  colCitations: "Citations received",
  yearToDate: "{year} (so far)",
  yearsNote:
    "Citations received in each year are to all of the journal's works, not only that year's. The current year is incomplete.",
  copySummary: "Copy summary",
  copied: "Copied!",
  summarySource: "Source: OpenAlex (not Clarivate JIF or Scopus CiteScore)",
  openAlexLink: "View on OpenAlex",
  homepageLink: "Journal website",
} as const;

export type JournalMetricsLookupStrings = typeof JOURNAL_METRICS_LOOKUP_STRINGS;

interface Found {
  journals: JournalMetrics[];
  year: number;
}

export default function JournalMetricsLookupWidget() {
  const strings = useToolUi(JOURNAL_METRICS_LOOKUP_STRINGS);
  const fmt = useToolNumber();
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<Found | null>(null);
  const [chosen, setChosen] = useState(0);
  const [problem, setProblem] = useState<LookupProblem | null>(null);
  const [warning, setWarning] = useState(false);
  const run = useRun();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setWarning(false);
    const controller = run.start(1);
    try {
      const result = await lookUpJournal(query, controller.signal);
      if (!run.isCurrent(controller)) {
        return;
      }
      setChosen(0);
      if (result.ok) {
        setFound({ journals: result.journals, year: new Date().getFullYear() });
        setWarning(result.badCheckDigit);
      } else {
        setFound(null);
        setProblem(result.problem);
      }
    } catch {
      // Aborted by a newer lookup or by leaving the page: nothing to report.
    } finally {
      run.finish(controller);
    }
  }

  const journal = found?.journals[chosen];
  return (
    <section className="space-y-6">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <TextField
          hint={strings.inputHint}
          id="jml-query"
          label={strings.inputLabel}
          onChange={setQuery}
          placeholder={strings.inputPlaceholder}
          type="search"
          value={query}
        />
        <button
          className={BUTTON_PRIMARY}
          disabled={run.running || query.trim() === ""}
          type="submit"
        >
          {run.running ? strings.looking : strings.lookUp}
        </button>
      </form>
      <div aria-live="polite" className="space-y-6">
        {warning ? <Notice>{strings.badCheckDigit}</Notice> : null}
        {problem ? <ErrorNotice>{strings[problem]}</ErrorNotice> : null}
        {journal || problem || run.running ? null : (
          <Notice>{strings.emptyPrompt}</Notice>
        )}
        {found && journal ? (
          <div className={`${RESULT_CARD} space-y-5`}>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            {found.journals.length > 1 ? (
              <fieldset className="space-y-2">
                <legend className={LABEL}>{strings.matchesLabel}</legend>
                <div className="flex flex-wrap gap-2">
                  {found.journals.map((option, index) => (
                    <button
                      aria-pressed={index === chosen}
                      className={
                        index === chosen ? BUTTON_PRIMARY : BUTTON_SECONDARY
                      }
                      key={option.id}
                      onClick={() => setChosen(index)}
                      type="button"
                    >
                      {t(strings.matchOption, {
                        name: option.name,
                        works: fmt(option.works),
                      })}
                    </button>
                  ))}
                </div>
              </fieldset>
            ) : null}
            <JournalCard
              journal={journal}
              lookupYear={found.year}
              strings={strings}
            />
            <p className={HINT}>{strings.summarySource}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
